/**
 * CvGroupedBars — AVG customer value by a category (source or discount), the
 * Praxis "AVG CV by Discount / by Source" view. Each row is a category with its
 * CV at Day 1 / 30 / 60 / 90 days / 6 months / 12 months (light→dark bars) and
 * its cohort population. Maturity-gated values arrive null and render "—".
 * Hover any bar to see which timeframe it is + the value (founder 2026-06-15:
 * "if I hover the light-blue bar it should say 30 days" — esp. the long
 * discount list). Founder 2026-06-15.
 */

const money0 = (n: number): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

export type CvTf = "cv1" | "cv30" | "cv60" | "cv90" | "cv180" | "cv365";
export interface CvBarRow {
  label: string;
  cv1: number | null;
  cv30: number | null;
  cv60: number | null;
  cv90: number | null;
  cv180: number | null;
  cv365: number | null;
  customers: number;
}

/** Day 1 → 12 months, light→dark accent (like the reference's blues). */
const SERIES: Array<{ key: CvTf; label: string }> = [
  { key: "cv1", label: "Day 1" },
  { key: "cv30", label: "30 days" },
  { key: "cv60", label: "60 days" },
  { key: "cv90", label: "90 days" },
  { key: "cv180", label: "6 months" },
  { key: "cv365", label: "12 months" },
];
const shade = (i: number): string =>
  `color-mix(in srgb, var(--accent) ${Math.round(30 + (i / (SERIES.length - 1)) * 60)}%, var(--surface))`;

export function CvGroupedBars({ rows, onPick, max: maxRows = 8 }: {
  rows: CvBarRow[];
  onPick?: (label: string) => void;
  max?: number;
}): React.ReactElement {
  const shown = rows.filter((r) => SERIES.some((s) => (r[s.key] ?? 0) > 0)).slice(0, maxRows);
  const max = Math.max(1, ...shown.flatMap((r) => SERIES.map((s) => r[s.key] ?? 0)));
  if (shown.length === 0) return <div style={{ color: "var(--faint)", fontSize: 12.5, padding: "10px 0" }}>No data yet.</div>;
  return (
    <div>
      {shown.map((r) => (
        <div key={r.label} onClick={onPick ? () => onPick(r.label) : undefined}
          className={onPick ? "dd-drill-row" : undefined} title={onPick ? "Click to drill in" : undefined}
          style={{ padding: "8px 4px", borderRadius: 8, cursor: onPick ? "pointer" : "default", marginBottom: 6 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
            <span style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }} title={r.label}>{r.label}</span>
            <span className="dd-num" style={{ fontSize: 11, color: "var(--body-faint)", whiteSpace: "nowrap" }}>{r.customers.toLocaleString()} cust</span>
          </div>
          {SERIES.map((s, i) => {
            const v = r[s.key];
            return (
              <div key={s.key} title={`${s.label}: ${v === null || v === undefined ? "not mature yet" : money0(v)}`}
                style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 2 }}>
                <div style={{ flex: 1, height: 10, background: "var(--surface-2)", borderRadius: 4, overflow: "hidden" }}>
                  <div style={{ width: `${Math.max(0, ((v ?? 0) / max) * 100)}%`, height: "100%", background: shade(i), borderRadius: 4 }} />
                </div>
                <span className="dd-num" style={{ width: 56, textAlign: "right", fontSize: 11.5, color: v === null || v === undefined ? "var(--faint)" : "var(--num)", fontWeight: 600 }}>
                  {v === null || v === undefined ? "—" : money0(v)}
                </span>
              </div>
            );
          })}
        </div>
      ))}
      <div style={{ display: "flex", gap: 12, marginTop: 8, color: "var(--body)", fontSize: 11.5, flexWrap: "wrap" }}>
        {SERIES.map((s, i) => (
          <span key={s.key}><i style={{ display: "inline-block", width: 10, height: 10, borderRadius: 3, marginRight: 5, verticalAlign: -1, background: shade(i) }} />{s.label}</span>
        ))}
      </div>
    </div>
  );
}
